import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { BrandLockup } from "@/components/brand";

const ICON = "/brand/app-icon-default-1024.png";
const ICON_DARK = "/brand/app-icon-dark-1024.png";
const ICON_TINTED = "/brand/app-icon-tinted-1024.png";

const SIZES = [1024, 512, 180, 120, 60, 48];

const MASKS = [
  { label: "Quadrada arredondada", radius: "22%" },
  { label: "Circular", radius: "50%" },
  { label: "Squircle", radius: "38%" },
];

const TOKENS = [
  ["brand.primary", "#033B8E"],
  ["brand.primaryDark", "#02275E"],
  ["brand.primaryLight", "#E7EDF7"],
  ["brand.accent", "#BF2832"],
  ["brand.silver", "#B4BAC7"],
  ["brand.background", "#F6F8FC"],
  ["brand.surface", "#FEFEFE"],
  ["brand.textPrimary", "#101828"],
  ["brand.textSecondary", "#4A5568"],
  ["brand.border", "#DCE1EA"],
  ["semantic.success", "#116B45"],
  ["semantic.warning", "#8A5300"],
  ["semantic.error", "#BF2832"],
  ["semantic.info", "#033B8E"],
];

const LOGOS = [
  ["Símbolo (fundo transparente)", "/brand/logo-symbol-transparent.png", "bg-white"],
  ["Vertical", "/brand/logo-vertical.png", "bg-white"],
  ["Horizontal", "/brand/logo-horizontal.png", "bg-white"],
  ["Fundo branco", "/brand/logo-on-white.png", "bg-white"],
  ["Fundo azul escuro", "/brand/logo-on-dark.png", "bg-sage-900"],
  ["Monocromática", "/brand/logo-mono.png", "bg-white"],
  ["Cabeçalho do portal", "/brand/logo-portal-header.png", "bg-white"],
  ["Tela de login", "/brand/logo-login.png", "bg-white"],
  ["Favicon", "/brand/favicon.png", "bg-white"],
];

const Section = ({ title, description, children, testId }) => (
  <section data-testid={testId} className="mt-16">
    <h2 className="font-display text-lg font-semibold text-ink-900 md:text-lg">{title}</h2>
    {description ? <p className="mt-2 max-w-2xl text-sm text-ink-500">{description}</p> : null}
    <div className="mt-6">{children}</div>
  </section>
);

export default function BrandBoard() {
  return (
    <div data-testid="brand-board" className="min-h-screen bg-bone">
      <header className="border-b border-border bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <BrandLockup size="sm" product="Identidade visual" testId="brand-board-lockup" />
          <Link
            to="/"
            data-testid="brand-board-back"
            className="inline-flex items-center gap-2 text-sm font-semibold text-sage-600"
          >
            <ArrowLeft size={16} /> Voltar
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-12">
        <p className="er-label">Prancha interna de validação</p>
        <h1 className="mt-3 max-w-3xl font-display text-4xl font-semibold tracking-tight text-ink-900 sm:text-5xl">
          Marca Erimus aplicada aos três produtos
        </h1>
        <p className="mt-5 max-w-2xl text-base leading-relaxed text-ink-600">
          Página interna de conferência dos ativos gerados a partir do arquivo oficial do logo. O símbolo não foi
          redesenhado: todas as versões derivam do mesmo arquivo original preservado.
        </p>

        <Section
          title="Versões do logo"
          description="Geradas por script a partir do arquivo original, com remoção apenas do fundo externo."
          testId="brand-logos"
        >
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {LOGOS.map(([label, src, bg]) => (
              <figure key={src} className="er-card">
                <div className={`grid h-40 place-items-center rounded-xl ${bg}`}>
                  <img src={src} alt={label} className="max-h-32 max-w-full object-contain" />
                </div>
                <figcaption className="mt-3 text-sm text-ink-600">{label}</figcaption>
              </figure>
            ))}
          </div>
        </Section>

        <Section
          title="Ícone dos aplicativos em vários tamanhos"
          description="Mesma composição para Erimus – Saúde Mental e Erimus – Rede de Apoio: símbolo acima e a palavra Erimus abaixo."
          testId="brand-icon-sizes"
        >
          <div className="er-card flex flex-wrap items-end gap-6">
            {SIZES.map((s) => (
              <div key={s} className="text-center">
                <img
                  src={ICON}
                  alt={`Ícone ${s} pixels`}
                  style={{ width: Math.min(s, 180), height: Math.min(s, 180) }}
                  className="rounded-[22%] border border-border"
                />
                <p className="mt-2 text-xs text-ink-500">{s} px</p>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Fundo claro e fundo escuro" testId="brand-icon-backgrounds">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
            {[
              ["Padrão em fundo claro", ICON, "bg-white"],
              ["Padrão em fundo escuro", ICON, "bg-ink-900"],
              ["Variante escura", ICON_DARK, "bg-ink-900"],
              ["Variante tonalizada", ICON_TINTED, "bg-sand"],
            ].map(([label, src, bg]) => (
              <figure key={label} className={`rounded-2xl border border-border p-6 ${bg}`}>
                <img src={src} alt={label} className="mx-auto h-32 w-32 rounded-[22%]" />
                <figcaption className={`mt-3 text-center text-xs ${bg === "bg-ink-900" ? "text-white/80" : "text-ink-600"}`}>
                  {label}
                </figcaption>
              </figure>
            ))}
          </div>
        </Section>

        <Section
          title="Máscaras do Android e do iOS"
          description="Conteúdo mantido dentro da área central segura para não ser recortado por nenhuma máscara."
          testId="brand-icon-masks"
        >
          <div className="er-card flex flex-wrap gap-10">
            {MASKS.map((m) => (
              <div key={m.label} className="text-center">
                <div
                  className="h-32 w-32 overflow-hidden border border-border"
                  style={{ borderRadius: m.radius }}
                >
                  <img src="/brand/android-adaptive-foreground.png" alt={m.label} className="h-full w-full bg-white" />
                </div>
                <p className="mt-2 text-xs text-ink-500">{m.label}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Simulação de tela inicial" testId="brand-homescreen">
          <div className="flex flex-wrap gap-8">
            {[
              ["bg-sage-900", "text-white"],
              ["bg-sand", "text-ink-700"],
            ].map(([bg, tone]) => (
              <div key={bg} className={`w-64 rounded-3xl p-6 ${bg}`}>
                <div className="grid grid-cols-3 gap-5">
                  {["Saúde Mental", "Rede de Apoio", "Portal"].map((name) => (
                    <div key={name} className="text-center">
                      <img src={ICON} alt={name} className="h-14 w-14 rounded-[22%]" />
                      <p className={`mt-1 text-[9px] leading-tight ${tone}`}>{name}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Splash screens" testId="brand-splash">
          <div className="flex flex-wrap gap-8">
            {[
              ["Erimus – Saúde Mental", "/brand/splash-saude.png"],
              ["Erimus – Rede de Apoio", "/brand/splash-apoio.png"],
            ].map(([label, src]) => (
              <figure key={src} className="er-card">
                <img src={src} alt={label} className="h-72 w-auto rounded-xl border border-border" />
                <figcaption className="mt-3 text-sm text-ink-600">{label}</figcaption>
              </figure>
            ))}
          </div>
        </Section>

        <Section title="Tokens de design" description="Fonte única da identidade em tailwind.config.js e index.css." testId="brand-tokens">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TOKENS.map(([name, hex]) => (
              <div key={name} className="er-card flex items-center gap-4">
                <span className="h-12 w-12 shrink-0 rounded-xl border border-border" style={{ background: hex }} />
                <span>
                  <span className="block text-sm font-semibold text-ink-800">{name}</span>
                  <span className="block text-xs text-ink-500">{hex}</span>
                </span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Tipografia" testId="brand-typography">
          <div className="er-card space-y-3">
            <p className="font-display text-4xl font-semibold text-ink-900">Erimus — Inter SemiBold</p>
            <p className="text-lg text-ink-700">Subtítulo em Inter Medium, acolhedor e legível.</p>
            <p className="text-base text-ink-600">
              Corpo de texto em Inter Regular, pensado para leitura confortável no celular e no notebook.
            </p>
            <p className="text-sm text-ink-500">Texto de apoio e legendas.</p>
          </div>
        </Section>
      </main>
    </div>
  );
}
